import React, { useState, useEffect } from "react";
import { Box, Paper, Grid, TextField, Button, Typography, CircularProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import Sidebar from "../Sidebar/Sidebar";
import useAuth from "../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import {
  fetchTenantInfoAdmin,
  updateTenantInfo,
  updateTenantInfoWithFiles,
  getTenantLogoUrl,
  getTenantBackgroundUrl,
} from "../../services/tenantService";
import ImageTile from "./ImageTile";

export default function TenantSettingsPage() {
  const { isLoggedIn, user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [tenant, setTenant] = useState(null);
  const [form, setForm] = useState({ address: "", phone: "", email: "" });
  const [files, setFiles] = useState({ logo: null, bg: null });
  const [preview, setPreview] = useState({ logo: null, bg: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const run = async () => {
      try {
        const tt = await fetchTenantInfoAdmin();
        setTenant(tt);
        setForm({
          address: tt.address || "",
          phone: tt.phone || "",
          email: tt.email || "",
        });
        if (tt?.id) setPreview({ logo: getTenantLogoUrl(tt.id), bg: getTenantBackgroundUrl(tt.id) });
      } catch (e) {
        enqueueSnackbar(t("tenantSettingsPage.errors.fetchFailed", "Chyba pri načítaní informácií o organizácii."), { variant: "error" });
      } finally {
        setLoading(false);
      }
    };
    if (isLoggedIn) run();
  }, [isLoggedIn, enqueueSnackbar, t]);

  useEffect(() => () => {
    Object.values(preview).forEach((u) => u?.startsWith("blob:") && URL.revokeObjectURL(u));
  }, [preview]);

  const onPick = (name, file) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (name === "logo_file") {
      preview.logo?.startsWith("blob:") && URL.revokeObjectURL(preview.logo);
      setFiles((p) => ({ ...p, logo: file }));
      setPreview((p) => ({ ...p, logo: url }));
    } else {
      preview.bg?.startsWith("blob:") && URL.revokeObjectURL(preview.bg);
      setFiles((p) => ({ ...p, bg: file }));
      setPreview((p) => ({ ...p, bg: url }));
    }
  };

  const removeImg = async (key) => {
    if (!tenant?.id) return;
    setSaving(true);
    try {
      await updateTenantInfo(tenant.id, key === "logo" ? { clear_logo: true } : { clear_login_photo: true });
      preview[key]?.startsWith("blob:") && URL.revokeObjectURL(preview[key]);
      setFiles((p) => ({ ...p, [key]: null }));
      setPreview((p) => ({ ...p, [key]: null }));

      enqueueSnackbar(
        key === "logo"
          ? t("tenantSettingsPage.messages.logoRemoved", "Logo removed")
          : t("tenantSettingsPage.messages.backgroundRemoved", "Background removed"),
        { variant: "success" }
      );
    } catch (e) {
      enqueueSnackbar(e.message || t("tenantSettingsPage.errors.removeFailed", "Failed to remove"), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!tenant?.id) return;
    setSaving(true);
    try {
      await updateTenantInfoWithFiles(tenant.id, {
        ...form,
        ...(files.logo ? { logo_file: files.logo } : {}),
        ...(files.bg ? { login_photo_file: files.bg } : {}),
      });

      if (tenant?.id) {
        if (files.logo) setPreview((p) => ({ ...p, logo: getTenantLogoUrl(tenant.id) + `?t=${Date.now()}` }));
        if (files.bg) setPreview((p) => ({ ...p, bg: getTenantBackgroundUrl(tenant.id) + `?t=${Date.now()}` }));
      }

      Object.values(preview).forEach((u) => u?.startsWith("blob:") && URL.revokeObjectURL(u));
      setFiles({ logo: null, bg: null });

      enqueueSnackbar(t("tenantSettingsPage.messages.saved", "Saved"), { variant: "success" });
    } catch (e) {
      enqueueSnackbar(e.message || t("tenantSettingsPage.errors.saveFailed", "Error saving"), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (!isLoggedIn || loading) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Sidebar user={user} />
      <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <Box sx={{ width: "100%", maxWidth: 1000 }}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("tenantSettingsPage.sections.tenant", "Organizácia")}
            </Typography>

            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <Typography>
                  <b>{t("tenantSettingsPage.fields.name", "Name")}:</b> {tenant?.name}
                </Typography>
                <Typography>
                  <b>{t("tenantSettingsPage.fields.slug", "Slug")}:</b> {tenant?.slug}
                </Typography>
                <Typography>
                  <b>{t("tenantSettingsPage.fields.description", "Description")}:</b> {tenant?.description || "—"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography>
                  <b>{t("tenantSettingsPage.fields.active", "Active")}:</b>{" "}
                  {tenant?.is_active ? t("tenantSettingsPage.values.yes", "Yes") : t("tenantSettingsPage.values.no", "No")}
                </Typography>
                <Typography>
                  <b>{t("tenantSettingsPage.fields.created", "Created")}:</b>{" "}
                  {new Date(tenant?.created_at).toLocaleString()}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("tenantSettingsPage.sections.publicInfo", "Public Info")}
            </Typography>

            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <ImageTile
                  label={t("tenantSettingsPage.images.logo", "Logo")}
                  name="logo_file"
                  src={preview.logo}
                  onPick={onPick}
                  onRemove={() => removeImg("logo")}
                  ratio="1 / 1"
                  hint={t("tenantSettingsPage.images.logoHint", "PNG/SVG")}
                  objectFit="contain"
                  disabled={saving}
                  size="sm"
                />
              </Grid>

              <Grid item xs={12} sm={8}>
                <ImageTile
                  label={t("tenantSettingsPage.images.background", "Login background")}
                  name="login_photo_file"
                  src={preview.bg}
                  onPick={onPick}
                  onRemove={() => removeImg("bg")}
                  ratio="16 / 9"
                  hint={t("tenantSettingsPage.images.backgroundHint", "JPG/PNG")}
                  objectFit="cover"
                  disabled={saving}
                  size="md"
                />
              </Grid>
            </Grid>

            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("tenantSettingsPage.fields.address", "Address")}
                  name="address"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, [e.target.name]: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("tenantSettingsPage.fields.phone", "Phone")}
                  name="phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, [e.target.name]: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("tenantSettingsPage.fields.email", "Email")}
                  name="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, [e.target.name]: e.target.value })}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, textAlign: "right" }}>
              <Button variant="contained" onClick={save} disabled={saving}>
                {saving
                  ? t("tenantSettingsPage.actions.saving", "Saving...")
                  : t("tenantSettingsPage.actions.save", "Save")}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}
